/**
 * Query Key Factory
 *
 * Centralized query keys for TanStack Query cache management.
 * Uses factory pattern for consistent key generation.
 */

// Helper type for filter objects
type Filters = { [key: string]: unknown };

export const queryKeys = {
  // Auth
  auth: {
    all: ['auth'] as const,
    me: () => [...queryKeys.auth.all, 'me'] as const,
  },

  // Global search (unified search endpoint)
  search: {
    all: ['search'] as const,
    global: (q: string, limit: number) =>
      [...queryKeys.search.all, 'global', { q, limit }] as const,
  },

  // Persons (henkilörekisteri — epic #763)
  persons: {
    all: ['persons'] as const,
    lists: () => [...queryKeys.persons.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.persons.lists(), filters] as const,
    details: () => [...queryKeys.persons.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.persons.details(), id] as const,
    companies: (id: string) => [...queryKeys.persons.detail(id), 'companies'] as const,
  },

  // Companies (yritysrekisteri — epic #763)
  companies: {
    all: ['companies'] as const,
    lists: () => [...queryKeys.companies.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.companies.lists(), filters] as const,
    details: () => [...queryKeys.companies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.companies.details(), id] as const,
    personLinks: (id: string) => [...queryKeys.companies.detail(id), 'personLinks'] as const,
  },

  // Vehicles
  vehicles: {
    all: ['vehicles'] as const,
    lists: () => [...queryKeys.vehicles.all, 'list'] as const,
    list: (filters: Filters) =>
      [...queryKeys.vehicles.lists(), filters] as const,
    details: () => [...queryKeys.vehicles.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.vehicles.details(), id] as const,
    history: (id: string) =>
      [...queryKeys.vehicles.detail(id), 'history'] as const,
    mileage: (id: string) =>
      [...queryKeys.vehicles.detail(id), 'mileage'] as const,
    mileageHistory: (id: string) =>
      [...queryKeys.vehicles.detail(id), 'mileageHistory'] as const,
    lookup: (registrationNumber: string) =>
      [...queryKeys.vehicles.all, 'lookup', registrationNumber] as const,
    stats: () => [...queryKeys.vehicles.all, 'stats'] as const,
    // Makes present in the fleet, with their models and settings (#2201).
    // Unfiltered: the list is small enough that the page searches it in place.
    makes: () => [...queryKeys.vehicles.all, 'makes'] as const,
  },

  // Bookings
  bookings: {
    all: ['bookings'] as const,
    lists: () => [...queryKeys.bookings.all, 'list'] as const,
    list: (filters: Filters) =>
      [...queryKeys.bookings.lists(), filters] as const,
    details: () => [...queryKeys.bookings.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.bookings.details(), id] as const,
    calendar: (start: string, end: string) =>
      [...queryKeys.bookings.all, 'calendar', { start, end }] as const,
    skillRequirements: (id: string) =>
      [...queryKeys.bookings.detail(id), 'skillRequirements'] as const,
    resourceValidation: (id: string) =>
      [...queryKeys.bookings.detail(id), 'resourceValidation'] as const,
    schedulingAvailability: (params: Filters) =>
      [...queryKeys.bookings.all, 'schedulingAvailability', params] as const,
    schedulingSlots: (params: Filters) =>
      [...queryKeys.bookings.all, 'schedulingSlots', params] as const,
  },

  // Work Orders (bookings with items)
  workOrders: {
    all: ['workOrders'] as const,
    lists: () => [...queryKeys.workOrders.all, 'list'] as const,
    list: (filters: Filters) =>
      [...queryKeys.workOrders.lists(), filters] as const,
    details: () => [...queryKeys.workOrders.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workOrders.details(), id] as const,
    timeEntries: (id: string) => [...queryKeys.workOrders.detail(id), 'timeEntries'] as const,
    notes: (id: string) => [...queryKeys.workOrders.detail(id), 'notes'] as const,
    history: (id: string) => [...queryKeys.workOrders.detail(id), 'history'] as const,
    parties: (id: string) => [...queryKeys.workOrders.detail(id), 'parties'] as const,
    partsStatus: (id: string) => [...queryKeys.workOrders.detail(id), 'partsStatus'] as const,
    // Filters are optional so invalidation can keep calling `stats()` — the
    // unfiltered key is a prefix of every filtered one (#2154).
    stats: (filters?: Filters) =>
      filters
        ? ([...queryKeys.workOrders.all, 'stats', filters] as const)
        : ([...queryKeys.workOrders.all, 'stats'] as const),
    myServices: (filters?: Filters) =>
      filters
        ? ([...queryKeys.workOrders.all, 'myServices', filters] as const)
        : ([...queryKeys.workOrders.all, 'myServices'] as const),
    flow: () => [...queryKeys.workOrders.all, 'flow'] as const,
  },

  // Skills
  skills: {
    all: ['skills'] as const,
    lists: () => [...queryKeys.skills.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.skills.lists(), filters] as const,
    details: () => [...queryKeys.skills.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.skills.details(), id] as const,
    serviceTypes: () => [...queryKeys.skills.all, 'serviceTypes'] as const,
  },

  // Services
  services: {
    all: ['services'] as const,
    lists: () => [...queryKeys.services.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.services.lists(), filters] as const,
    details: () => [...queryKeys.services.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.services.details(), id] as const,
  },

  // Mechanic Skills
  mechanicSkills: {
    all: ['mechanicSkills'] as const,
    byUser: (userId: string) =>
      [...queryKeys.mechanicSkills.all, 'user', userId] as const,
  },

  // Lifts
  lifts: {
    all: ['lifts'] as const,
    lists: () => [...queryKeys.lifts.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.lifts.lists(), filters] as const,
    details: () => [...queryKeys.lifts.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.lifts.details(), id] as const,
    available: (startTime: string, endTime: string, vehicleId?: string) =>
      [...queryKeys.lifts.all, 'available', { startTime, endTime, vehicleId }] as const,
    status: () => [...queryKeys.lifts.all, 'status'] as const,
    utilization: (startDate: string, endDate: string) =>
      [...queryKeys.lifts.all, 'utilization', { startDate, endDate }] as const,
  },

  laborRates: {
    all: ['laborRates'] as const,
    lists: () => [...queryKeys.laborRates.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.laborRates.lists(), filters] as const,
  },

  // Workshop equipment (#2323) — everything that is not a lift
  workshopEquipment: {
    all: ['workshopEquipment'] as const,
    lists: () => [...queryKeys.workshopEquipment.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.workshopEquipment.lists(), filters] as const,
    details: () => [...queryKeys.workshopEquipment.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workshopEquipment.details(), id] as const,
  },

  // Parking
  parking: {
    all: ['parking'] as const,
    zones: {
      all: ['parking', 'zones'] as const,
      lists: () => [...queryKeys.parking.zones.all, 'list'] as const,
      list: () => [...queryKeys.parking.zones.lists()] as const,
      details: () => [...queryKeys.parking.zones.all, 'detail'] as const,
      detail: (id: string) => [...queryKeys.parking.zones.details(), id] as const,
    },
    spots: {
      all: ['parking', 'spots'] as const,
      available: () => [...queryKeys.parking.spots.all, 'available'] as const,
      byZone: (zoneId: string) => [...queryKeys.parking.spots.all, 'zone', zoneId] as const,
    },
    current: () => [...queryKeys.parking.all, 'current'] as const,
    utilization: (startDate: string, endDate: string) =>
      [...queryKeys.parking.all, 'utilization', { startDate, endDate }] as const,
  },

  // Invoices
  invoices: {
    all: ['invoices'] as const,
    lists: () => [...queryKeys.invoices.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.invoices.lists(), filters] as const,
    details: () => [...queryKeys.invoices.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.invoices.details(), id] as const,
    forWorkOrder: (workOrderId: string) => [...queryKeys.invoices.all, 'workOrder', workOrderId] as const,
  },

  // Live invoice preview for one work order. Tax/discount variants sit under
  // this prefix so invalidation stays scoped to the affected booking (#2643).
  // Callable form is kept for existing callers (`queryKeys.invoicePreview(id)`);
  // `.all` is the family prefix assistant writes invalidate (#2607).
  invoicePreview: Object.assign(
    (workOrderId: string) => ['invoicePreview', workOrderId] as const,
    { all: ['invoicePreview'] as const },
  ),

  /**
   * Status-history rows keyed under the legacy `work-orders` prefix
   * (`useUndoWorkOrder` invalidates `['work-orders', id, 'history']`).
   * Distinct from `workOrders` (`['workOrders']`).
   */
  workOrderHistory: {
    all: ['work-orders'] as const,
  },

  workOrderParts: {
    all: ['workOrderParts'] as const,
  },

  // Inspection Templates
  inspectionTemplates: {
    all: ['inspectionTemplates'] as const,
    lists: () => [...queryKeys.inspectionTemplates.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.inspectionTemplates.lists(), filters] as const,
    details: () => [...queryKeys.inspectionTemplates.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.inspectionTemplates.details(), id] as const,
  },

  // Inspections
  inspections: {
    all: ['inspections'] as const,
    lists: () => [...queryKeys.inspections.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.inspections.lists(), filters] as const,
    details: () => [...queryKeys.inspections.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.inspections.details(), id] as const,
    photos: (id: string) => [...queryKeys.inspections.detail(id), 'photos'] as const,
    recommendations: (id: string) => [...queryKeys.inspections.detail(id), 'recommendations'] as const,
    reports: (id: string) => [...queryKeys.inspections.detail(id), 'reports'] as const,
    stats: () => [...queryKeys.inspections.all, 'stats'] as const,
  },

  // Approvals
  approvals: {
    all: ['approvals'] as const,
    lists: () => [...queryKeys.approvals.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.approvals.lists(), filters] as const,
    details: () => [...queryKeys.approvals.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.approvals.details(), id] as const,
    stats: () => [...queryKeys.approvals.all, 'stats'] as const,
  },

  // Dashboard
  dashboard: {
    all: ['dashboard'] as const,
    stats: () => [...queryKeys.dashboard.all, 'stats'] as const,
    layout: () => [...queryKeys.dashboard.all, 'layout'] as const,
  },

  // Inventory
  inventory: {
    all: ['inventory'] as const,
    parts: {
      all: ['inventory', 'parts'] as const,
      lists: () => [...queryKeys.inventory.parts.all, 'list'] as const,
      list: (filters: Filters) => [...queryKeys.inventory.parts.lists(), filters] as const,
      details: () => [...queryKeys.inventory.parts.all, 'detail'] as const,
      detail: (id: string) => [...queryKeys.inventory.parts.details(), id] as const,
    },
    suppliers: {
      all: ['inventory', 'suppliers'] as const,
      lists: () => [...queryKeys.inventory.suppliers.all, 'list'] as const,
      list: (filters: Filters) => [...queryKeys.inventory.suppliers.lists(), filters] as const,
      details: () => [...queryKeys.inventory.suppliers.all, 'detail'] as const,
      detail: (id: string) => [...queryKeys.inventory.suppliers.details(), id] as const,
    },
    purchaseOrders: {
      all: ['inventory', 'purchaseOrders'] as const,
      lists: () => [...queryKeys.inventory.purchaseOrders.all, 'list'] as const,
      list: (filters: Filters) => [...queryKeys.inventory.purchaseOrders.lists(), filters] as const,
      details: () => [...queryKeys.inventory.purchaseOrders.all, 'detail'] as const,
      detail: (id: string) => [...queryKeys.inventory.purchaseOrders.details(), id] as const,
    },
    categories: () => [...queryKeys.inventory.all, 'categories'] as const,
    warehouses: () => [...queryKeys.inventory.all, 'warehouses'] as const,
    stockLevels: () => [...queryKeys.inventory.all, 'stockLevels'] as const,
    expiring: (days: number) => [...queryKeys.inventory.all, 'expiring', days] as const,
    returns: (status?: string) =>
      status
        ? ([...queryKeys.inventory.all, 'returns', status] as const)
        : ([...queryKeys.inventory.all, 'returns'] as const),
    warrantyClaims: (status?: string) =>
      status
        ? ([...queryKeys.inventory.all, 'warrantyClaims', status] as const)
        : ([...queryKeys.inventory.all, 'warrantyClaims'] as const),
    stats: () => [...queryKeys.inventory.all, 'stats'] as const,
    transitWarehouse: (filters?: Filters) =>
      filters
        ? ([...queryKeys.inventory.all, 'transitWarehouse', filters] as const)
        : ([...queryKeys.inventory.all, 'transitWarehouse'] as const),
    partsDashboard: (filters?: Filters) =>
      filters
        ? ([...queryKeys.inventory.all, 'partsDashboard', filters] as const)
        : ([...queryKeys.inventory.all, 'partsDashboard'] as const),
  },

  // Reports
  reports: {
    all: ['reports'] as const,
    revenue: (params: Filters) => [...queryKeys.reports.all, 'revenue', params] as const,
    summary: (params: Filters) => [...queryKeys.reports.all, 'summary', params] as const,
    mechanicPerformance: (params: Filters) => [...queryKeys.reports.all, 'mechanicPerformance', params] as const,
    serviceTypes: (params: Filters) => [...queryKeys.reports.all, 'serviceTypes', params] as const,
    monthlyComparison: (year?: number) => [...queryKeys.reports.all, 'monthlyComparison', year] as const,
    customerStats: (params: Filters) => [...queryKeys.reports.all, 'customerStats', params] as const,
    customerSatisfaction: (params: Filters) =>
      [...queryKeys.reports.all, 'customerSatisfaction', params] as const,
    agentPerformance: (params: Filters) =>
      [...queryKeys.reports.all, 'agentPerformance', params] as const,
    workOrderDurationPerformance: (params: Filters) =>
      [...queryKeys.reports.all, 'workOrderDurationPerformance', params] as const,
  },

  // Checklist Templates
  checklistTemplates: {
    all: ['checklistTemplates'] as const,
    lists: () => [...queryKeys.checklistTemplates.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.checklistTemplates.lists(), filters] as const,
    details: () => [...queryKeys.checklistTemplates.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.checklistTemplates.details(), id] as const,
  },

  // Booking Checklists
  checklists: {
    all: ['checklists'] as const,
    byBooking: (bookingId: string) => [...queryKeys.checklists.all, 'booking', bookingId] as const,
    details: () => [...queryKeys.checklists.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.checklists.details(), id] as const,
  },

  // WorkPath Templates
  workPathTemplates: {
    all: ['workPathTemplates'] as const,
    lists: () => [...queryKeys.workPathTemplates.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.workPathTemplates.lists(), filters] as const,
    details: () => [...queryKeys.workPathTemplates.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workPathTemplates.details(), id] as const,
  },

  // Booking WorkPaths
  workPaths: {
    all: ['workPaths'] as const,
    byBooking: (bookingId: string) =>
      [...queryKeys.workPaths.all, 'booking', bookingId] as const,
    details: () => [...queryKeys.workPaths.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.workPaths.details(), id] as const,
  },

  // Tenant
  tenant: {
    all: ['tenant'] as const,
    me: () => [...queryKeys.tenant.all, 'me'] as const,
    settings: () => [...queryKeys.tenant.all, 'settings'] as const,
    floorPlan: () => [...queryKeys.tenant.all, 'floor-plan'] as const,
  },

  // Opening-hours exceptions (#1282)
  hoursExceptions: {
    all: ['hoursExceptions'] as const,
    list: (from?: string, to?: string) =>
      [...queryKeys.hoursExceptions.all, 'list', from ?? null, to ?? null] as const,
  },

  // Assistant
  assistant: {
    all: ['assistant'] as const,
    conversations: () => [...queryKeys.assistant.all, 'conversations'] as const,
    messages: (conversationId: string) =>
      [...queryKeys.assistant.all, 'messages', conversationId] as const,
    preferences: () => [...queryKeys.assistant.all, 'preferences'] as const,
  },

  // Assistant insights (proactive OS Buddy inbox — epic #1082, issue #1090)
  insights: {
    all: ['insights'] as const,
    lists: () => [...queryKeys.insights.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.insights.lists(), filters] as const,
    details: () => [...queryKeys.insights.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.insights.details(), id] as const,
    mutedTypes: () => [...queryKeys.insights.all, 'muted-types'] as const,
  },
  // Resend delivery state of customer email (#3787)
  outboundEmails: {
    all: ['outboundEmails'] as const,
    /** Pages of one subject (infinite query); `pageSize` keeps differently sized pages apart. */
    list: (entityType: string, entityId: string, pageSize: number) =>
      [...queryKeys.outboundEmails.all, entityType, entityId, pageSize] as const,
  },
  // Quote sends of a work order, the ids their emails carry (#3787). Not under
  // outboundEmails: a delivery event must not refetch the send list.
  workOrderQuoteSends: (workOrderId: string) => ['workOrderQuoteSends', workOrderId] as const,
  exceptions: {
    all: ['exceptions'] as const,
    lists: () => [...queryKeys.exceptions.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.exceptions.lists(), filters] as const,
    details: () => [...queryKeys.exceptions.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.exceptions.details(), id] as const,
  },

  // Shifts
  shifts: {
    all: ['shifts'] as const,
    patterns: {
      all: ['shifts', 'patterns'] as const,
      lists: () => [...queryKeys.shifts.patterns.all, 'list'] as const,
      list: (filters: Filters) => [...queryKeys.shifts.patterns.lists(), filters] as const,
      details: () => [...queryKeys.shifts.patterns.all, 'detail'] as const,
      detail: (id: string) => [...queryKeys.shifts.patterns.details(), id] as const,
    },
    schedule: (params: Filters) => [...queryKeys.shifts.all, 'schedule', params] as const,
    overrides: {
      all: ['shifts', 'overrides'] as const,
      lists: () => [...queryKeys.shifts.overrides.all, 'list'] as const,
      list: (filters: Filters) => [...queryKeys.shifts.overrides.lists(), filters] as const,
    },
  },

  // Attachments
  attachments: {
    all: ['attachments'] as const,
    byEntity: (entityType: string, entityId: string) =>
      [...queryKeys.attachments.all, entityType, entityId] as const,
    byEntities: (entities: { entityType: string; entityId: string }[]) =>
      [...queryKeys.attachments.all, 'multi', entities] as const,
  },

  // Tasks
  tasks: {
    all: ['tasks'] as const,
    lists: () => [...queryKeys.tasks.all, 'list'] as const,
    list: (filters: Filters) => [...queryKeys.tasks.lists(), filters] as const,
    details: () => [...queryKeys.tasks.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.tasks.details(), id] as const,
  },

  // HR
  hr: {
    all: ['hr'] as const,
    sickLeaves: {
      all: ['hr', 'sickLeaves'] as const,
      lists: () => [...queryKeys.hr.sickLeaves.all, 'list'] as const,
      list: (filters: Filters) => [...queryKeys.hr.sickLeaves.lists(), filters] as const,
      details: () => [...queryKeys.hr.sickLeaves.all, 'detail'] as const,
      detail: (id: string) => [...queryKeys.hr.sickLeaves.details(), id] as const,
    },
    vacationRequests: {
      all: ['hr', 'vacationRequests'] as const,
      lists: () => [...queryKeys.hr.vacationRequests.all, 'list'] as const,
      list: (filters: Filters) => [...queryKeys.hr.vacationRequests.lists(), filters] as const,
      details: () => [...queryKeys.hr.vacationRequests.all, 'detail'] as const,
      detail: (id: string) => [...queryKeys.hr.vacationRequests.details(), id] as const,
    },
    balances: {
      all: ['hr', 'balances'] as const,
      details: () => [...queryKeys.hr.balances.all, 'detail'] as const,
      detail: (employeeId: string, year: number) =>
        [...queryKeys.hr.balances.details(), employeeId, year] as const,
    },
    history: {
      all: ['hr', 'history'] as const,
      byEmployee: (employeeId: string, filters: Filters) =>
        [...queryKeys.hr.history.all, employeeId, filters] as const,
    },
    timeTracking: {
      all: ['hr', 'timeTracking'] as const,
      currentSession: () => [...queryKeys.hr.timeTracking.all, 'current'] as const,
      entries: (filters: Filters) => [...queryKeys.hr.timeTracking.all, 'entries', filters] as const,
      clockTimes: (from: string, to: string) => [...queryKeys.hr.timeTracking.all, 'clockTimes', { from, to }] as const,
    },
  },

  workshopRules: {
    all: ['workshopRules'] as const,
    list: () => [...queryKeys.workshopRules.all, 'list'] as const,
  },

  correctionSignals: {
    all: ['correctionSignals'] as const,
    list: () => [...queryKeys.correctionSignals.all, 'list'] as const,
  },

  // CoPaya portal entry (#1806)
  copaya: {
    all: ['copaya'] as const,
    status: () => [...queryKeys.copaya.all, 'status'] as const,
  },

  // Staff messaging (PF-113 header unread badge)
  conversations: {
    all: ['conversations'] as const,
    unreadCount: () => [...queryKeys.conversations.all, 'unreadCount'] as const,
  },

  // Staff-notification bell (PF-135; shared with PF-136 later)
  notifications: {
    all: ['notifications'] as const,
    list: () => [...queryKeys.notifications.all, 'list'] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unreadCount'] as const,
  },
};
